/**
 * Unified Content Context Types
 *
 * This module defines the single source of truth for context data used across:
 * - Chat conversations
 * - Write page / content pieces
 * - AI feedback features (tone fit, rewrite, fact check, document analysis)
 * - Projects (default context for groups of documents)
 *
 * Design Principles:
 * 1. Single type used everywhere (no more ChatContext vs useWriteContext differences)
 * 2. Hierarchical inheritance: Project defaults → Document overrides
 * 3. Nullable fields mean "inherit from parent" when in a project context
 */

/**
 * Core content context - the unified context type.
 * Used for Chat, Write, AI features, and Projects.
 */
export interface ContentContext {
  /** Personal writing style ID (mutually exclusive with brandProfileId) */
  writingStyleId?: string | null;

  /** Brand profile ID (mutually exclusive with writingStyleId) */
  brandProfileId?: string | null;

  /** Content purpose/goal (e.g., "Convert leads to customers") */
  purpose?: string | null;

  /** Publishing channel (e.g., "email", "linkedin", "blog_post") */
  channel?: string | null;

  /** Target audience description */
  targetAudience?: string | null;

  /** SEO target keywords */
  seoKeywords?: string[] | null;

  /** Story/narrative framework (e.g., "PAS", "AIDA", "BAB") */
  storyFramework?: string | null;
}

/**
 * Extended context with document-specific fields.
 * Used when working with a specific content piece.
 */
export interface DocumentContext extends ContentContext {
  /** Current document content (for AI analysis) */
  currentContent?: string;

  /** Selected text from editor (for targeted AI feedback) */
  selectedText?: string;

  /** Content piece ID if editing existing content */
  contentId?: string;

  /** Conversation ID if linked to a chat */
  conversationId?: string;
}

/**
 * Project with default context settings.
 * All documents in a project inherit these defaults unless overridden.
 */
export interface Project {
  id: string;
  userId: string;
  name: string;
  description?: string | null;

  /** Default context applied to all documents in this project */
  defaultWritingStyleId?: string | null;
  defaultBrandProfileId?: string | null;
  defaultPurpose?: string | null;
  defaultChannel?: string | null;
  defaultTargetAudience?: string | null;
  defaultSeoKeywords?: string[] | null;
  defaultStoryFramework?: string | null;

  createdAt: string;
  updatedAt: string;
}

/**
 * Context resolution result showing inheritance chain.
 * Useful for UI to show what's inherited vs overridden.
 */
export interface ResolvedContext {
  /** Final merged context (project defaults + document overrides) */
  effective: ContentContext;

  /** Values inherited from project (not set on document) */
  inherited: Partial<ContentContext>;

  /** Values explicitly set on document (overriding project) */
  overridden: Partial<ContentContext>;

  /** Source project ID if context came from a project */
  projectId?: string;
}

/**
 * Context source tracking for debugging and UI display.
 */
export type ContextSource = 'project' | 'document' | 'conversation' | 'manual';

/**
 * Context field metadata for UI display.
 */
export interface ContextFieldMeta {
  field: keyof ContentContext;
  value: unknown;
  source: ContextSource;
  sourceId?: string;
  sourceName?: string;
}

// ============================================================================
// Adapter Functions - Convert between old and new context formats
// ============================================================================

import type { ChatContext } from '@/api/chat';

/**
 * Convert legacy ChatContext to unified ContentContext.
 */
export function fromChatContext(chat: ChatContext): ContentContext {
  return {
    writingStyleId: chat.writing_style_id,
    brandProfileId: chat.brand_profile_id,
    purpose: chat.purpose,
    channel: chat.channel,
    targetAudience: chat.target_audience,
    seoKeywords: chat.seo_keywords,
    storyFramework: chat.story_framework,
  };
}

/**
 * Convert unified ContentContext to legacy ChatContext format.
 * Includes optional document-specific fields.
 */
export function toChatContext(
  ctx: ContentContext,
  documentFields?: {
    currentContent?: string;
    selectedText?: string;
    contentId?: string;
  }
): ChatContext {
  return {
    writing_style_id: ctx.writingStyleId ?? undefined,
    brand_profile_id: ctx.brandProfileId ?? undefined,
    purpose: ctx.purpose ?? undefined,
    channel: ctx.channel ?? undefined,
    target_audience: ctx.targetAudience ?? undefined,
    seo_keywords: ctx.seoKeywords ?? undefined,
    story_framework: ctx.storyFramework ?? undefined,
    current_content: documentFields?.currentContent,
    selected_text: documentFields?.selectedText,
    content_id: documentFields?.contentId,
  };
}

/**
 * Convert database content_pieces row to ContentContext.
 */
export function fromContentPiece(piece: {
  writing_style_id?: string | null;
  brand_profile_id?: string | null;
  purpose_description?: string | null;
  medium?: string | null;
  custom_medium?: string | null;
  target_audience?: string | null;
  target_keywords?: string[] | null;
  story_framework?: string | null;
}): ContentContext {
  // Resolve channel from medium/custom_medium
  const channel = piece.medium === 'custom'
    ? piece.custom_medium
    : piece.medium;

  return {
    writingStyleId: piece.writing_style_id,
    brandProfileId: piece.brand_profile_id,
    purpose: piece.purpose_description,
    channel: channel,
    targetAudience: piece.target_audience,
    seoKeywords: piece.target_keywords,
    storyFramework: piece.story_framework,
  };
}

/**
 * Convert ContentContext to content_pieces database format.
 */
export function toContentPieceFields(ctx: ContentContext): {
  writing_style_id: string | null;
  brand_profile_id: string | null;
  purpose_description: string | null;
  medium: string | null;
  custom_medium: string | null;
  target_audience: string | null;
  target_keywords: string[] | null;
  story_framework: string | null;
} {
  // Determine if channel is a standard medium or custom
  const standardMediums = [
    'seo_article', 'blog_post', 'newsletter', 'email', 'website',
    'linkedin', 'twitter', 'video_script'
  ];

  const channel = ctx.channel ?? null;
  const isStandardMedium = channel !== null && standardMediums.includes(channel);

  return {
    writing_style_id: ctx.writingStyleId ?? null,
    brand_profile_id: ctx.brandProfileId ?? null,
    purpose_description: ctx.purpose ?? null,
    medium: isStandardMedium ? channel : (channel !== null ? 'custom' : null),
    custom_medium: isStandardMedium ? null : channel,
    target_audience: ctx.targetAudience ?? null,
    target_keywords: ctx.seoKeywords ?? null,
    story_framework: ctx.storyFramework ?? null,
  };
}

/**
 * Convert Project database row to Project type.
 */
export function fromProjectRow(row: {
  id: string;
  user_id: string;
  name: string;
  description?: string | null;
  default_writing_style_id?: string | null;
  default_brand_profile_id?: string | null;
  default_purpose?: string | null;
  default_channel?: string | null;
  default_target_audience?: string | null;
  default_seo_keywords?: string[] | null;
  default_story_framework?: string | null;
  created_at: string;
  updated_at: string;
}): Project {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    description: row.description,
    defaultWritingStyleId: row.default_writing_style_id,
    defaultBrandProfileId: row.default_brand_profile_id,
    defaultPurpose: row.default_purpose,
    defaultChannel: row.default_channel,
    defaultTargetAudience: row.default_target_audience,
    defaultSeoKeywords: row.default_seo_keywords,
    defaultStoryFramework: row.default_story_framework,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Get default context from a Project.
 */
export function getProjectDefaultContext(project: Project): ContentContext {
  return {
    writingStyleId: project.defaultWritingStyleId,
    brandProfileId: project.defaultBrandProfileId,
    purpose: project.defaultPurpose,
    channel: project.defaultChannel,
    targetAudience: project.defaultTargetAudience,
    seoKeywords: project.defaultSeoKeywords,
    storyFramework: project.defaultStoryFramework,
  };
}

// ============================================================================
// Context Resolution - Merge project defaults with document overrides
// ============================================================================

/**
 * Resolve effective context by merging project defaults with document overrides.
 * Document values take precedence; null/undefined means "inherit from project".
 */
export function resolveContext(
  projectDefaults: ContentContext | null,
  documentOverrides: ContentContext
): ResolvedContext {
  const inherited: Partial<ContentContext> = {};
  const overridden: Partial<ContentContext> = {};
  const effective: ContentContext = {};

  const fields: (keyof ContentContext)[] = [
    'writingStyleId',
    'brandProfileId',
    'purpose',
    'channel',
    'targetAudience',
    'seoKeywords',
    'storyFramework',
  ];

  for (const field of fields) {
    const docValue = documentOverrides[field];
    const projectValue = projectDefaults?.[field];

    if (docValue !== undefined && docValue !== null) {
      // Document has explicit value - use it
      (effective as any)[field] = docValue;
      (overridden as any)[field] = docValue;
    } else if (projectValue !== undefined && projectValue !== null) {
      // Inherit from project
      (effective as any)[field] = projectValue;
      (inherited as any)[field] = projectValue;
    }
    // Otherwise field remains undefined (no value from either source)
  }

  return {
    effective,
    inherited,
    overridden,
    projectId: projectDefaults ? undefined : undefined, // Will be set by caller
  };
}

/**
 * Check if a context field is inherited (not overridden on document).
 */
export function isFieldInherited(
  resolved: ResolvedContext,
  field: keyof ContentContext
): boolean {
  return field in resolved.inherited && !(field in resolved.overridden);
}

/**
 * Check if context has any values set.
 */
export function hasContextValues(ctx: ContentContext): boolean {
  return !!(
    ctx.writingStyleId ||
    ctx.brandProfileId ||
    ctx.purpose ||
    ctx.channel ||
    ctx.targetAudience ||
    (ctx.seoKeywords && ctx.seoKeywords.length > 0) ||
    ctx.storyFramework
  );
}

/**
 * Create an empty context.
 */
export function emptyContext(): ContentContext {
  return {
    writingStyleId: null,
    brandProfileId: null,
    purpose: null,
    channel: null,
    targetAudience: null,
    seoKeywords: null,
    storyFramework: null,
  };
}

/**
 * Merge two contexts, with second taking precedence.
 *
 * Important: Explicit `null` values in override will clear the field.
 * Only `undefined` means "keep the base value".
 *
 * This allows: updateContext({ channel: null }) to clear the channel.
 */
export function mergeContexts(
  base: ContentContext,
  override: Partial<ContentContext>
): ContentContext {
  // Helper to handle the merge logic:
  // - undefined in override = keep base value
  // - null in override = explicitly clear (set to null)
  // - any other value = use override value
  const merge = <T>(baseVal: T, overrideVal: T | undefined): T => {
    return overrideVal === undefined ? baseVal : overrideVal;
  };

  return {
    writingStyleId: merge(base.writingStyleId, override.writingStyleId),
    brandProfileId: merge(base.brandProfileId, override.brandProfileId),
    purpose: merge(base.purpose, override.purpose),
    channel: merge(base.channel, override.channel),
    targetAudience: merge(base.targetAudience, override.targetAudience),
    seoKeywords: merge(base.seoKeywords, override.seoKeywords),
    storyFramework: merge(base.storyFramework, override.storyFramework),
  };
}
